import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
// JD Simplified styles are already included in the main index.css
import { AuthProvider } from './lib/auth';
import { initializeTheme } from './lib/theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UsermavenAnalyticsProvider } from './contexts/UsermavenContext';
import { initChunkErrorHandler } from './lib/chunk-error-handler';
import { initVersionCheck } from './lib/version-check';
import { initializeCapacitor, isNativeApp } from './lib/capacitor';

// Initialize chunk error handler to handle app updates gracefully
initChunkErrorHandler();

// Initialize proactive version check (polls /version.json for new deploys)
initVersionCheck();

// Initialize theme before rendering the app
initializeTheme();

// Pre-rendered SEO content (#seo-content) is hidden by default via CSS
// and removed from the DOM in App.tsx once React mounts

// Initialize Capacitor for native platforms
// This sets up status bar, keyboard handling, and app lifecycle listeners
// Note: Deep links are initialized separately in App.tsx with router access
if (isNativeApp) {
  initializeCapacitor().catch(err => {
    console.error('[Capacitor] Initialization failed:', err);
  });
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in v5)
      refetchOnWindowFocus: false,
      placeholderData: undefined, // replaces keepPreviousData
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  // React StrictMode intentionally mounts components twice in development to help find issues
  // This is why you might see two AuthProvider mounts in the console - it's normal in dev mode
  // IMPORTANT: There should only be ONE AuthProvider in the entire app tree.
  // This double mounting is ONLY in development and doesn't indicate a bug.
  // DO NOT add additional AuthProviders in your route components!
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <UsermavenAnalyticsProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </UsermavenAnalyticsProvider>
      {/* Temporarily disabled for testing */}
      {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  </React.StrictMode>
);